<template>
  <div class="statistics">
    <!-- Sélecteur de métrique -->
    <div class="selector-section">
      <label for="metric-select">Sélectionner une métrique :</label>
      <select v-model="selectedMetric" id="metric-select" class="metric-selector">
        <option value="poids">Poids (kg)</option>
        <option value="imc">IMC</option>
        <option value="graisse_corporelle">Graisse Corporelle (%)</option>
        <option value="frequence_cardiaque">Fréquence Cardiaque (BPM)</option>
        <option value="indice_cardiaque">Indice Cardiaque</option>
        <option value="taux_musculaire">Taux Musculaire (%)</option>
        <option value="poids_sans_graisse">Poids sans Graisse (kg)</option>
        <option value="graisse_sous_cutanee">Graisse Sous-cutanée (%)</option>
        <option value="graisse_viscerale">Graisse Viscérale</option>
        <option value="eau_corporelle">Eau Corporelle (%)</option>
        <option value="muscle_squelettique">Muscle Squelettique (%)</option>
        <option value="masse_musculaire">Masse Musculaire (kg)</option>
        <option value="masse_osseuse">Masse Osseuse (kg)</option>
        <option value="proteine">Protéine (%)</option>
        <option value="taux_metabolique_basal">Taux Métabolique Basal (kcal)</option>
        <option value="age_corporel">Âge Corporel (ans)</option>
      </select>
    </div>

    <!-- Graphique ou message si pas de données -->
    <div v-show="hasData" class="chart-section">
      <Line v-if="hasData" :key="selectedMetric" :data="chartData" :options="chartOptions" />
    </div>

    <div v-show="!hasData" class="no-data">
      <p>Aucune donnée disponible pour cette métrique.</p>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import { fr } from 'date-fns/locale'

// Enregistrer les composants Chart.js nécessaires
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
)

export default {
  name: 'Statistics',
  components: {
    Line
  },
  props: {
    measurements: {
      type: Array,
      required: true
    },
    currentUser: {
      type: String,
      required: true
    }
  },
  setup(props) {
    // État : métrique sélectionnée
    const selectedMetric = ref('poids')

    // Définition des métriques avec labels
    const allMetrics = ref([
      { key: 'poids', label: 'Poids', unit: 'kg' },
      { key: 'imc', label: 'IMC', unit: '' },
      { key: 'graisse_corporelle', label: 'Graisse Corporelle', unit: '%' },
      { key: 'frequence_cardiaque', label: 'Fréquence Cardiaque', unit: 'BPM' },
      { key: 'indice_cardiaque', label: 'Indice Cardiaque', unit: '' },
      { key: 'taux_musculaire', label: 'Taux Musculaire', unit: '%' },
      { key: 'poids_sans_graisse', label: 'Poids sans Graisse', unit: 'kg' },
      { key: 'graisse_sous_cutanee', label: 'Graisse Sous-cutanée', unit: '%' },
      { key: 'graisse_viscerale', label: 'Graisse Viscérale', unit: '' },
      { key: 'eau_corporelle', label: 'Eau Corporelle', unit: '%' },
      { key: 'muscle_squelettique', label: 'Muscle Squelettique', unit: '%' },
      { key: 'masse_musculaire', label: 'Masse Musculaire', unit: 'kg' },
      { key: 'masse_osseuse', label: 'Masse Osseuse', unit: 'kg' },
      { key: 'proteine', label: 'Protéine', unit: '%' },
      { key: 'taux_metabolique_basal', label: 'Taux Métabolique Basal', unit: 'kcal' },
      { key: 'age_corporel', label: 'Âge Corporel', unit: 'ans' }
    ])

    // Obtenir les informations de la métrique courante
    const currentMetricInfo = computed(() => {
      return allMetrics.value.find(m => m.key === selectedMetric.value) || {}
    })

    // Formater le label avec l'unité
    const getMetricLabel = computed(() => {
      const info = currentMetricInfo.value
      return info.unit ? `${info.label} (${info.unit})` : info.label
    })

    // Traiter les données pour le graphique
    const processedData = computed(() => {
      const metric = selectedMetric.value

      try {
        // Filtrer et transformer les mesures
        const dataPoints = props.measurements
          .filter(m => {
            const val = m.data?.[metric]?.value
            return val !== null && val !== undefined && val !== ''
          })
          .map(m => {
            try {
              // Parser la date française DD/MM/YYYY
              const [day, month, year] = m.date.split('/')
              const dateTime = new Date(`${year}-${month}-${day}T${m.time || '00:00'}`)

              // Vérifier si la date est valide
              if (isNaN(dateTime.getTime())) {
                return null
              }

              return {
                x: dateTime,
                y: parseFloat(m.data[metric].value)
              }
            } catch (error) {
              console.warn('Erreur de parsing de date:', m.date, error)
              return null
            }
          })
          .filter(point => point !== null)
          .sort((a, b) => a.x - b.x) // Tri chronologique

        return dataPoints
      } catch (error) {
        console.error('Erreur lors du traitement des données:', error)
        return []
      }
    })

    // Vérifier si des données existent
    const hasData = computed(() => {
      return processedData.value.length > 0
    })

    // Données pour le graphique
    const chartData = computed(() => {
      const data = processedData.value
      if (!data || data.length === 0) {
        return { datasets: [] }
      }

      return {
        datasets: [{
          label: getMetricLabel.value,
          data: data,
          borderColor: '#667eea',
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#667eea',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }]
      }
    })

    // Options du graphique
    const chartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#2c3e50',
            font: {
              size: 14,
              weight: '500'
            },
            padding: 15,
            usePointStyle: true
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleFont: {
            size: 14,
            weight: '600'
          },
          bodyFont: {
            size: 13
          },
          padding: 12,
          displayColors: false,
          callbacks: {
            title: (tooltipItems) => {
              const date = new Date(tooltipItems[0].parsed.x)
              return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            },
            label: (context) => {
              const value = context.parsed.y
              const unit = currentMetricInfo.value.unit
              return `${value}${unit ? ' ' + unit : ''}`
            }
          }
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'day',
            displayFormats: {
              day: 'dd/MM/yyyy'
            },
            tooltipFormat: 'dd/MM/yyyy HH:mm'
          },
          adapters: {
            date: {
              locale: fr
            }
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
            drawBorder: false
          },
          ticks: {
            color: '#6c757d',
            font: {
              size: 12
            }
          },
          title: {
            display: true,
            text: 'Date',
            color: '#2c3e50',
            font: {
              size: 13,
              weight: '600'
            }
          }
        },
        y: {
          beginAtZero: false,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
            drawBorder: false
          },
          ticks: {
            color: '#6c757d',
            font: {
              size: 12
            }
          },
          title: {
            display: true,
            text: getMetricLabel.value,
            color: '#2c3e50',
            font: {
              size: 13,
              weight: '600'
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    }))

    return {
      selectedMetric,
      chartData,
      chartOptions,
      hasData
    }
  }
}
</script>

<style scoped>
.statistics {
  width: 100%;
}

.selector-section {
  margin-bottom: 2rem;
}

.selector-section label {
  display: block;
  color: #2c3e50;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 1rem;
}

.metric-selector {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background-color: white;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.3s ease;
}

.metric-selector:hover {
  border-color: #667eea;
}

.metric-selector:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.chart-section {
  height: 400px;
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.no-data {
  padding: 3rem 2rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.no-data p {
  color: #6c757d;
  font-size: 1.1rem;
  margin: 0;
}

@media (max-width: 768px) {
  .chart-section {
    height: 300px;
    padding: 1rem;
  }

  .selector-section label {
    font-size: 0.9rem;
  }

  .metric-selector {
    font-size: 0.9rem;
    padding: 0.6rem 0.8rem;
  }
}
</style>
